import {
  BRAND_REFRESH_SHOW_WELCOME_SESSION_KEY,
  BRAND_REFRESH_START_NAV_TOUR_EVENT,
  BRAND_REFRESH_WELCOME_CLEARED_EVENT,
  BRAND_REFRESH_WELCOME_QUEUED_EVENT,
} from './constants'

/**
 * Whether `sessionStorage` is available (false in some SSR / non-browser environments).
 */
function isSessionStorageAvailable(): boolean {
  return typeof sessionStorage !== 'undefined'
}

/**
 * Whether `window` is available (browser / jsdom).
 */
function isWindowAvailable(): boolean {
  return typeof window !== 'undefined'
}

/**
 * Dispatches a same-tab CustomEvent on `window` when available.
 */
function dispatchWindowEvent(eventName: string): void {
  if (!isWindowAvailable()) {
    return
  }
  window.dispatchEvent(new CustomEvent(eventName))
}

/**
 * Marks the Brand Refresh welcome modal as pending in sessionStorage.
 * Does not dispatch a window event.
 */
export function setBrandRefreshWelcomeSessionFlag(): void {
  if (!isSessionStorageAvailable()) {
    return
  }
  sessionStorage.setItem(BRAND_REFRESH_SHOW_WELCOME_SESSION_KEY, 'true')
}

/**
 * Marks the Brand Refresh welcome modal as pending in sessionStorage, then dispatches a
 * window event so same-tab listeners can open it without remounting.
 */
export function queueBrandRefreshWelcomeModal(): void {
  if (!isSessionStorageAvailable()) {
    return
  }
  setBrandRefreshWelcomeSessionFlag()
  dispatchWindowEvent(BRAND_REFRESH_WELCOME_QUEUED_EVENT)
}

/**
 * Clears Brand Refresh welcome pending from sessionStorage, then dispatches a window event
 * so same-tab listeners can dismiss it (and show dependent UI such as the nav tour card).
 */
export function clearBrandRefreshWelcomeModalQueue(): void {
  if (!isSessionStorageAvailable()) {
    return
  }
  sessionStorage.removeItem(BRAND_REFRESH_SHOW_WELCOME_SESSION_KEY)
  dispatchWindowEvent(BRAND_REFRESH_WELCOME_CLEARED_EVENT)
}

/**
 * Reads sessionStorage for whether the Brand Refresh welcome modal is pending.
 * Does not dispatch or listen for window events.
 */
export function shouldShowBrandRefreshWelcomeModal(): boolean {
  if (!isSessionStorageAvailable()) {
    return false
  }
  return sessionStorage.getItem(BRAND_REFRESH_SHOW_WELCOME_SESSION_KEY) === 'true'
}

/**
 * Dispatches a window event asking the nav (which owns the tour refs) to start the Brand
 * Refresh nav Spotlight tour. Does not read or write sessionStorage.
 */
export function startBrandRefreshNavTour(): void {
  dispatchWindowEvent(BRAND_REFRESH_START_NAV_TOUR_EVENT)
}
